import useTaxCustomer from '../taxcontext/useTaxCustomer'
import CustomerContextSelector from '../taxcontext/CustomerContextSelector'
// DashboardLanding.js
import { Link } from "react-router-dom"
import useAuth from "../../hooks/useAuth"
import useTitle from "../../hooks/useTitle"
import TaxpayerContextSelector from "../taxcontext/TaxpayerContextSelector"
import { useGetTaxpayersQuery } from "../taxpayers/taxpayersApiSlice"

const appMeta = {
    PAJAK_AUTOMATION: {
        title: "Pajak Automation",
        description: "Kelola withholding docs, ringkasan pajak, dan monitoring data perpajakan.",
        path: "/dash/tax"
    },
    POS_APPLICATION: {
        title: "POS Application",
        description: "Kelola order, makanan, minuman, dan operasional kasir.",
        path: "/dash/orders"
    },
    PROPERTY_ASSISTANT: {
        title: "Property Assistant",
        description: "Kelola data properti dan kebutuhan operasional agen.",
        path: "/dash"
    },
    EASY_NEWSROOM: {
        title: "easyNewsroom",
        description: "Kelola campaign, notes, dan content support.",
        path: "/dash/campaigns"
    },
    DOCUMENT_EVALUATOR: {
        title: "Document Evaluator",
        description: "Kelola evaluasi dokumen dan alur review.",
        path: "/dash"
    },
    MONGODB_APPLICATION: {
        title: "MongoDB Application",
        description: "Akses tools dan fitur aplikasi MongoDB internal.",
        path: "/dash"
    },
    MYSQL_APPLICATION: {
        title: "MySQL Application",
        description: "Akses fitur aplikasi berbasis MySQL.",
        path: "/dash/sales"
    },
    MQTT_APPLICATION: {
        title: "MQTT Application",
        description: "Monitoring aplikasi dan data IoT berbasis MQTT.",
        path: "/dash/fishFlow"
    }
}

const DashboardLanding = () => {
    useTitle("Dashboard")

    const {
        username,
        displayname,
        platformRoles,
        appAssignments,
        accessibleApps,
        isGlobalAdmin,
        getAppAccess
    } = useAuth()

    const customerId = useTaxCustomer()
    const pajakAccess = getAppAccess("PAJAK_AUTOMATION")
    const {
        currentData: taxpayers = [],
        isFetching: isLoading,
        error,
        isError,
        } = useGetTaxpayersQuery({ customer_id: customerId, limit: 200 }, {
        skip: !pajakAccess || !customerId, // only fetch if user has Pajak access
    })

    const appsToShow = isGlobalAdmin
        ? Object.keys(appMeta)
        : accessibleApps

    const pajakCustomerId = pajakAccess?.scope?.customer_id ?? "-"
    const pajakTaxpayerId = pajakAccess?.scope?.taxpayer_id ?? "-"

    return (
        <section className="p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                    <h1 className="text-2xl font-bold text-slate-800">Welcome to Mudah Panel</h1>
                    <p className="text-slate-500 mt-1">
                        Pilih aplikasi yang ingin Anda buka dari workspace ini.
                    </p>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                            <div className="text-slate-500">Display Name</div>
                            <div className="font-semibold text-slate-800">{displayname || "-"}</div>
                        </div>

                        <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                            <div className="text-slate-500">Username</div>
                            <div className="font-semibold text-slate-800">{username || "-"}</div>
                        </div>

                        <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                            <div className="text-slate-500">Platform Role</div>
                            <div className="font-semibold text-slate-800">
                                {isGlobalAdmin ? "GLOBAL_ADMIN" : "Scoped User"}
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                            <div className="text-slate-500">Accessible Applications</div>
                            <div className="font-semibold text-slate-800">
                                {appsToShow.length > 0 ? appsToShow.length : 0}
                            </div>
                        </div>
                    </div>

                    {platformRoles?.length > 0 && (
                        <div className="mt-4">
                            <div className="text-sm text-slate-500 mb-2">Platform Roles</div>
                            <div className="flex flex-wrap gap-2">
                                {platformRoles.map((role) => (
                                    <span
                                        key={role}
                                        className="px-3 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700"
                                    >
                                        {role}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isGlobalAdmin && pajakAccess && (
                        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <div className="text-sm font-semibold text-emerald-800">Pajak Scope</div>
                            <div className="text-sm text-emerald-700 mt-1">
                                customer_id: <span className="font-semibold">{pajakCustomerId}</span> | taxpayer_id:{" "}
                                <span className="font-semibold">{pajakTaxpayerId}</span>
                            </div>
                        </div>
                    )}

                    {pajakAccess && (
                        <div className="mt-4">
                            <CustomerContextSelector />
                            {isError && <p role="alert" className="mb-3 text-sm text-red-700">{error?.data?.message || 'Gagal memuat taxpayer.'}</p>}
                            {customerId && !isError && <TaxpayerContextSelector
                                taxpayers={taxpayers}
                                isLoading={isLoading}
                                className="w-full max-w-md"
                            />}
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-slate-800">Your Applications</h2>
                        <span className="text-sm text-slate-500">
                            {isGlobalAdmin ? "Full platform access" : "Scoped application access"}
                        </span>
                    </div>

                    {appsToShow.length === 0 ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
                            No application access found for this account.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {appsToShow.map((appKey) => {
                                const meta = appMeta[appKey] || {
                                    title: appKey,
                                    description: "Application access available.",
                                    path: "/dash"
                                }

                                const assignment = appAssignments?.find((a) => a.app === appKey)
                                const roles = isGlobalAdmin
                                    ? ["GLOBAL_ADMIN"]
                                    : (assignment?.roles || [])

                                return (
                                    <div
                                        key={appKey}
                                        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-slate-800">
                                                    {meta.title}
                                                </h3>
                                                <p className="text-sm text-slate-500 mt-1">
                                                    {meta.description}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                                                Roles
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {roles.length > 0 ? (
                                                    roles.map((role) => (
                                                        <span
                                                            key={role}
                                                            className="px-2.5 py-1 text-xs rounded-full bg-slate-100 text-slate-700"
                                                        >
                                                            {role}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-slate-400">No explicit role</span>
                                                )}
                                            </div>
                                        </div>

                                        {!isGlobalAdmin && assignment?.scope && Object.keys(assignment.scope).length > 0 && (
                                            <div className="mt-4">
                                                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                                                    Scope
                                                </div>
                                                <div className="text-sm text-slate-600 space-y-1">
                                                    {Object.entries(assignment.scope).map(([key, value]) => (
                                                        <div key={key}>
                                                            <span className="font-medium">{key}</span>: {String(value)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-5">
                                            <Link
                                                to={meta.path}
                                                className="inline-flex items-center justify-center rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors"
                                            >
                                                Open Application
                                            </Link>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default DashboardLanding